import type { DoubleDatapoint, StringDatapoint } from '@cognite/sdk';

import type { ColumnSchema } from '#core/lib/transforms/column-schema.ts';

/**
 * A single CDF datapoint — either numeric or string-valued. Mirrors the SDK's
 * `DoubleDatapoint | StringDatapoint` discriminated union; re-exported as a
 * named alias so consumers don't have to spell the union themselves.
 */
export type Datapoint = DoubleDatapoint | StringDatapoint;

/**
 * Row-level value shape produced by timeseries enrichment.
 *
 * Replaces the previous `<col>` (bare ref) + `<col>_latestValue` + `<col>_unit`
 * companion-column convention. The column type stays `'ref'` with
 * `refKind: 'ts'`; only the row-level value shape is richer.
 *
 * Source-shape note: CDF returns instanceId-typed properties pointing at
 * timeseries as bare externalId strings, while entity-typed instanceId refs
 * come back as `{ space, externalId }`. The enrichment accepts either shape.
 * `space` is therefore optional — populated when the source value was a
 * full ref object or when CDF's datapoints response carries
 * `instanceId.space`, omitted otherwise.
 *
 * Empty-datapoint case: `datapoints` is `[]` and `start` / `end` /
 * `latestDatapoint` are omitted, keeping the schema consistent with the
 * "fetch returned nothing" branch.
 */
export interface EnrichedTimeseriesRef {
  /** CDF instance space — preserved from the source ref or recovered from CDF's response when known. */
  space?: string;
  /** CDF external id — always present. */
  externalId: string;
  /** Physical unit reported by CDF for the timeseries, when set. */
  unit?: string;
  /** Datapoints returned by `client.datapoints.retrieve`, oldest first. */
  datapoints: Datapoint[];
  /** Timestamp of the oldest datapoint in {@link datapoints}. Omitted when empty. */
  start?: Date;
  /** Timestamp of the newest datapoint in {@link datapoints}. Omitted when empty. */
  end?: Date;
  /** The most recent datapoint (last element of {@link datapoints}). Omitted when empty. */
  latestDatapoint?: Datapoint;
}

/**
 * Type guard for {@link EnrichedTimeseriesRef}. Distinguishes an enriched ref
 * from a bare ref ({@link InstanceReference}) or a bare externalId string by
 * the presence of a `datapoints` array.
 *
 * `space` is optional on the enriched shape (CDF returns timeseries
 * instanceId properties as bare externalId strings), so this guard
 * intentionally does not require an `InstanceReference` shape — only
 * `externalId: string` plus `datapoints: Datapoint[]`.
 */
export function isEnrichedTimeseriesRef(value: unknown): value is EnrichedTimeseriesRef {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as { externalId?: unknown; datapoints?: unknown };
  return typeof candidate.externalId === 'string' && Array.isArray(candidate.datapoints);
}
/**
 * Predicate for column-schema-level detection of enriched timeseries ref
 * columns: ref-typed columns whose `refKind` is `'ts'`. Single source of
 * truth shared by `QueryKnowledgeGraphTool` (LLM-facing message) and
 * `describe-views/helpers.ts::buildEnrichmentFooter` (schema doc footer)
 * — they were independently writing the same predicate and drifting on
 * cosmetics.
 */
export function isTimeseriesRefColumn(column: ColumnSchema): boolean {
  return column.type === 'ref' && column.refKind === 'ts';
}
